import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, AlertCircle } from "lucide-react";
import { personName } from "@/lib/format";
import { toast } from "sonner";

const EXAMPLES = [
  "Mostrami le pratiche urgenti non ancora assegnate",
  "Quali pratiche sono in lavorazione?",
  "Persone da richiamare questa settimana",
  "Pratiche concluse",
];

const AIAssistant = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => { api.get("/ai/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configurato: false, attivo: false })); }, []);

  const search = async (query) => {
    const text = query || q;
    if (!text.trim()) return;
    setLoading(true); setRes(null);
    try { const { data } = await api.post("/ai/search", { query: text }); setRes(data); }
    catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader icon={Sparkles} title="Assistente AI" description="Ricerca in linguaggio naturale e riassunti (modulo assistivo, non decisionale)" />
      {status && !status.configurato && (
        <Card className="p-4 mb-4 flex items-start gap-2 bg-[color:var(--brand-orange-100)] border-[color:var(--brand-orange-500)]">
          <AlertCircle className="h-5 w-5 text-[color:var(--brand-orange-600)] shrink-0" />
          <p className="text-sm">Funzione predisposta ma non ancora configurata.</p>
        </Card>
      )}
      <Card className="p-4">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Scrivi una domanda in italiano…" className="h-11" data-testid="ai-search-input" />
          <Button onClick={() => search()} disabled={loading} className="h-11 bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="ai-search-button">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLES.map((e) => <button key={e} onClick={() => { setQ(e); search(e); }} className="text-xs rounded-full border px-3 py-1 hover:bg-muted">{e}</button>)}
        </div>
      </Card>

      {res && (
        <div className="mt-4">
          {res.spiegazione && <p className="text-sm text-muted-foreground mb-3"><Sparkles className="h-3.5 w-3.5 inline mr-1 text-[color:var(--brand-orange-600)]" />{res.spiegazione} · {res.count} risultati</p>}
          <Card className="divide-y">
            {res.risultati.length === 0 ? <p className="p-6 text-center text-muted-foreground text-sm">Nessun risultato</p> : res.risultati.map((r) => (
              r.numero ? (
                <button key={r.id} onClick={() => navigate(`/app/pratiche/${r.id}`)} className="w-full text-left p-3 hover:bg-muted flex items-center justify-between gap-2">
                  <div><div className="font-medium text-sm">{r.numero}</div><div className="text-xs text-muted-foreground truncate">{r.servizio_nome}</div></div>
                  <div className="flex gap-2"><PriorityBadge priorita={r.priorita} /><StatusBadge stato={r.stato} /></div>
                </button>
              ) : (
                <div key={r.id} className="p-3 text-sm">{r.titolo || personName(r)}</div>
              )
            ))}
          </Card>
          <p className="text-[11px] text-muted-foreground mt-3">Risultati filtrati dall'assistente AI nel rispetto dei tuoi permessi. Verifica sempre le informazioni.</p>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
